import { API_URL } from '../services/constants';

interface ApiRequestOptions extends RequestInit {
  headers?: HeadersInit;
}

export async function makeApiRequest(
  endpoint: string,
  options: ApiRequestOptions = {},
) {
  const baseUrl = API_URL;
  const refreshTokenEndpoint = `${baseUrl}/user-refresh-token/generate-new-access-token`;
  const refreshAndAccessTokenEndpoint = `${baseUrl}/user-refresh-token/generate-new-refresh-and-access-token`;

  async function fetchWithRetry(retryCount = 0): Promise<any> {
    const accessToken = localStorage.getItem('accessToken');

    const headers = new Headers(options.headers);
    headers.set('Authorization', `Bearer ${accessToken}`);

    // Only set Content-Type for non-FormData requests
    if (!(options.body instanceof FormData)) {
      headers.set('Content-Type', 'application/json');
    }

    const response = await fetch(`${baseUrl}${endpoint}`, {
      ...options,
      headers,
    });

    // Check if response is JSON
    const contentType = response.headers.get('Content-Type') || '';
    let responseData;
    if (contentType.includes('application/json')) {
      responseData = await response.json();
    } else {
      responseData = { message: await response.text() };
    }

    const statusCode = responseData.status_code || response.status;
    if (statusCode === 401 && retryCount === 0) {
      const refreshToken = localStorage.getItem('refreshToken');

      if (!refreshToken) {
        // No refresh token available - clear all auth data and redirect to login
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('userData');
        localStorage.removeItem('device_id');
        window.location.href = '/auth/login';
        throw new Error(
          'Oturum Süreniz dolmuştur. Lütfen tekrar giriş yapın.',
        );
      }

      // Try to refresh access token
      const refreshResponse = await fetch(refreshTokenEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      const refreshData = await refreshResponse.json();

      if (refreshData.status_code === 401 || refreshData.error) {
        // Refresh token invalid or expired, try to get both new tokens
        const userData = JSON.parse(localStorage.getItem('userData') || '{}');
        const userId = userData.id;

        if (!userId) {
          // No user ID, clear tokens and redirect to login
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('userData');
          window.location.href = '/auth/login';
          throw new Error(
            'Oturum süreniz dolmuştur. Lütfen tekrar giriş yapın.',
          );
        }

        const newTokensResponse = await fetch(refreshAndAccessTokenEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            user_id: userId,
            previous_refresh_token: refreshToken,
          }),
        });

        const newTokensData = await newTokensResponse.json();

        if (newTokensData.status_code === 200 && !newTokensData.error) {
          const { access_token, refresh_token, device_id } = newTokensData.data;
          localStorage.setItem('accessToken', access_token);
          localStorage.setItem('refreshToken', refresh_token);
          // Optionally store device_id if returned
          if (device_id) {
            localStorage.setItem('device_id', device_id);
          }
          return await fetchWithRetry(retryCount + 1);
        } else {
          // Token not found or unauthorized - clear tokens and redirect to login
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('userData');
          localStorage.removeItem('device_id');
          window.location.href = '/auth/login';
          throw new Error(
            'Oturum süreniz dolmuştur. Lütfen tekrar giriş yapın.',
          );
        }
      }

      if (refreshData.status_code === 200 && !refreshData.error) {
        const { access_token } = refreshData.data;
        localStorage.setItem('accessToken', access_token);
        return await fetchWithRetry(retryCount + 1);
      }
    }

    if (!response.ok || responseData?.error) {
      const fallbackMessage =
        responseData?.message ||
        (Array.isArray(responseData?.detail)
          ? responseData.detail
              .map((item: any) => item?.msg || item?.message)
              .filter(Boolean)
              .join(', ')
          : responseData?.detail?.msg) ||
        'Bilinmeyen bir hata oluştu';
      return {
        error: fallbackMessage,
        status: statusCode,
      };
    }

    return {
      data: responseData.data,
      status: statusCode,
    };
  }

  try {
    return await fetchWithRetry();
  } catch (error) {
    return {
      error:
        error instanceof Error ? error.message : 'Bilinmeyen bir hata oluştu',
      status: 500,
    };
  }
}
